import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import DayPicker, { DateUtils, DayModifiers } from 'react-day-picker';
import { StyledCalendar } from './index.styles';
import clsx from 'clsx';
import moment from 'moment';
import MomentLocaleUtils from 'react-day-picker/moment';
import { CalendarProps, dateV } from './index.types';
import { isRTLEnabled } from './../../../utils/src/utils';

const CalendarIcon = ({ labelName = '', labelClassName = '', mandatory = false }) => {
  return (
    <div className={clsx(labelClassName)}>
      <span className="icon-calendar"></span>
      <span>
        {labelName}
        {mandatory ? '*' : ''}
      </span>
    </div>
  );
};

const TRCCalendar: React.FC<CalendarProps> = props => {
  const {
    type = 'single',
    numberOfMonths = 1,
    onSelect,
    onClearFlag,
    flexibleDatesFlag,
    fontClass,
    labelName,
    mandatory,
    isError,
    errorMessageClassName,
    errorMessage,
    labelClassName,
    disabled,
    id,
    placeholder,
    calendarConfig,
  } = props;
  const [showCalendar, setShowCalendar] = useState(false);
  const [state, setState] = useState<dateV['state']>(
    type === 'single'
      ? {
          selectedDay: null,
        }
      : {
          from: null,
          to: null,
          enteredTo: null,
        }
  );
  const { from, enteredTo } = state;
  const [dateText, setDateText] = useState(placeholder);

  const onClear = (flag: boolean) => {
    if (flag) {
      if (type === 'single') {
        setDateText(placeholder);
        setState({
          selectedDay: null,
        });
      } else {
        setDateText(placeholder);
        setState({
          from: null,
          to: null,
          enteredTo: null,
        });
      }
    }
  };

  useEffect(() => {
    onClear(onClearFlag);
    onClear(flexibleDatesFlag as boolean);
  }, [isError, onClearFlag, flexibleDatesFlag]);

  const handleDayMouseEnter = (day: Date) => {
    const { from, to } = state;
    if (!isSelectingFirstDay(from, to, day)) {
      setState({ ...state, enteredTo: day });
    }
  };
  const isSelectingFirstDay = (from: Date, to: Date, day: Date) => {
    const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from);
    const isRangeSelected = from && to;
    return !from || isBeforeFirstDay || isRangeSelected;
  };
  const handleRangeDayClick = (day: Date, modifiers: DayModifiers) => {
    if (modifiers['disabled']) {
      return;
    }
    const { from, to } = state;
    if (from && to && day >= from && day <= to) {
      return;
    }
    if (isSelectingFirstDay(from, to, day)) {
      setState({
        from: day,
        to: null,
        enteredTo: null,
      });
    } else {
      setState({
        ...state,
        to: day,
        enteredTo: day,
      });
    }
  };
  const handleSingleDayClick = (day: Date, modifiers: DayModifiers) => {
    if (modifiers['disabled']) {
      return;
    }
    setState({
      selectedDay: day,
    });
  };
  const modifiers = { start: from, end: enteredTo };
  const selectedDays = [from, { from, to: enteredTo }];
  const isSingleDate = type === 'single';
  const calendarRef = useRef(null);
  const [locale, setLocale] = useState('');
  useEffect(() => {
    if (!Object.values(state).includes(null) && onSelect) {
      if (isSingleDate) {
        if (state.selectedDay) {
          const formattedDate = moment(state.selectedDay).locale(locale).format('MMM DD,YYYY');
          setDateText(formattedDate.replace('.', ''));
        }
      } else {
        const selectedFromDate = moment(state.from).locale(locale).format('MMM DD,YYYY');
        const selectedToDate = moment(state.to).locale(locale).format('MMM DD,YYYY');
        const formattedDate = `${selectedFromDate.replace('.', '')} - ${selectedToDate.replace('.', '')}` as string;
        setDateText(formattedDate);
      }
      setShowCalendar(false);
      onSelect(state);
    }
  }, [state]);
  useEffect(() => {
    const docLang = document?.documentElement?.lang;
    const docLocale = (docLang && docLang.split(/_|-/)[0].toLowerCase()) || 'en';
    setLocale(docLocale);
    if (docLocale !== 'en') {
      if (docLang && docLang === 'zh-CN') {
        require(`moment/locale/zh-cn`);
      } else if (docLang && docLang === 'zh-TW') {
        require(`moment/locale/zh-hk`);
      } else {
        require(`moment/locale/${docLocale}`);
      }
    }
  }, []);

  /**
   * to set the prev. selected month open when we open the calender
   */
  const [month, setMonth] = useState<dateV['state']>(null);
  const calRef = useRef(null) as dateV['state'];

  useImperativeHandle(
    calRef,
    () => {
      /**
       * can pass the custom ref function to calendar
       */
      return {
        setDefaultMonth(date: Date) {
          setMonth(date);
        },
      };
    },
    []
  );

  /**
   * when calender is open shows the selcted month on UI
   */
  useEffect(() => {
    if (showCalendar == true && calRef?.current) {
      calRef?.current?.setDefaultMonth(type === 'single' ? state?.selectedDay : state?.from);
    }
  }, [showCalendar]);

  return (
    <StyledCalendar data-component-name="a-trc-TRCCalendar">
      <div className="rfp-calendar" ref={calRef}>
        <span id={`rfp-${id}`}></span>
        <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', padding: '10px 0px' }}>
          <CalendarIcon labelName={labelName} labelClassName={labelClassName} mandatory={mandatory} />
          <div
            className={clsx('calendar-date t-rc-alt-border-color d-flex align-items-center', isError ? 'error' : '')}
            id={`calendar-date_${id}`}
            role="textbox"
            aria-label={id}
            tabIndex={-1}
            style={{ borderWidth: '1px', borderStyle: 'solid', padding: '10px' }}
            onClick={() => {
              if (!disabled) {
                setShowCalendar(!showCalendar);
              }
            }}
          >
            {dateText}
          </div>
        </div>
        {showCalendar && (
          <DayPicker
            ref={calendarRef}
            dir={isRTLEnabled ? 'rtl' : 'ltr'}
            weekdaysShort={calendarConfig.weekdaysShort ?? []}
            weekdaysLong={calendarConfig.weekdaysLong ?? []}
            months={calendarConfig.monthsLong ?? []}
            className={`${fontClass} ${isSingleDate ? 'SingleDayPicker' : 'RangeDayPicker'}`}
            numberOfMonths={numberOfMonths}
            disabledDays={{ before: new Date() }}
            selectedDays={isSingleDate ? state.selectedDay : selectedDays}
            modifiers={modifiers}
            onDayClick={isSingleDate ? handleSingleDayClick : handleRangeDayClick}
            onDayMouseEnter={handleDayMouseEnter}
            locale={locale}
            localeUtils={MomentLocaleUtils}
            onMonthChange={(_month: Date): void => {
              setMonth(_month);
            }}
            {...{ month: month }}
          />
        )}
        {isError && <span className={clsx(errorMessageClassName, 'error-text t-font-xs')}>{errorMessage}</span>}
      </div>
    </StyledCalendar>
  );
};

export default TRCCalendar;
